import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocation, Link } from 'react-router-dom';
import ContactFormButton from '@/components/common/ContactFormButton';
import Logo from '@/components/common/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Services shown in the navbar "Services" dropdown (core services).
const SERVICE_LINKS = [
  { href: '/services/agents', label: 'AI Agents' },
  { href: '/services/ads', label: 'Advertising' },
  { href: '/services/automation', label: 'Automation' },
  { href: '/web-design', label: 'Websites' },
  { href: '/review-stands', label: 'Review Stands' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [navReady, setNavReady] = useState(false);
  const location = useLocation();

  // Initialize navbar after component mounts to ensure proper rendering
  useEffect(() => {
    // Set navbar ready state after mount
    const readyTimer = setTimeout(() => {
      setNavReady(true);
    }, 10);

    // Verify navbar elements after a delay
    const verifyTimer = setTimeout(() => {
      const navLinks = document.querySelector('.navbar-links');
      const navButtons = document.querySelector('.navbar-buttons');

      // Force display on desktop if needed
      if (window.innerWidth >= 768) {
        if (navLinks && window.getComputedStyle(navLinks).display === 'none') {
          (navLinks as HTMLElement).style.display = 'flex';
        }
        if (
          navButtons &&
          window.getComputedStyle(navButtons).display === 'none'
        ) {
          (navButtons as HTMLElement).style.display = 'flex';
        }
      }
    }, 200);

    return () => {
      clearTimeout(readyTimer);
      clearTimeout(verifyTimer);
    };
  }, [location.pathname]);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // "Services" is highlighted whenever any service page is active.
  const servicesActive = location.pathname.startsWith('/services');

  // Desktop nav styles - always visible on desktop
  const desktopNavLinksClasses = cn(
    'navbar-links items-center space-x-1 mx-auto',
    // Use conditional classes but also include inline style as fallback
    navReady ? 'flex' : 'hidden md:flex'
  );

  const desktopNavButtonsClasses = cn(
    'navbar-buttons',
    navReady ? 'flex' : 'hidden md:flex'
  );

  return (
    <div
      data-navbar="main"
      className="w-full flex justify-center pt-6 px-2 sm:px-4 fixed top-0 left-0 right-0 z-50"
    >
      <nav
        className={cn(
          'theme-nav rounded-full max-w-2xl transition-all duration-300 py-2 px-2 sm:px-3 backdrop-blur-md border shadow-lg w-full flex items-center justify-between',
          isScrolled && 'theme-nav--scrolled'
        )}
      >
        {/* Logo - Always visible */}
        <Link
          to="/"
          className="flex items-center"
          onClick={closeMobileMenu}
          aria-label="Elev8 Home"
        >
          <Logo className="h-10 md:h-12 w-auto" />
        </Link>

        {/* Desktop Navigation Links */}
        <div
          className={desktopNavLinksClasses}
          style={{
            display: window.innerWidth >= 768 ? 'flex' : 'none',
          }}
        >
          <NavItem href="/" isActive={location.pathname === '/'}>
            HOME
          </NavItem>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'theme-nav__link group inline-flex items-center gap-1 focus:outline-none',
                  servicesActive && 'theme-nav__link--active'
                )}
                aria-label="Services menu"
              >
                SERVICES
                <ChevronDown
                  size={16}
                  className="transition-transform duration-200 group-data-[state=open]:rotate-180"
                  aria-hidden="true"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              sideOffset={12}
              className="min-w-[12rem] rounded-2xl p-2"
            >
              {SERVICE_LINKS.map(service => (
                <DropdownMenuItem key={service.href} asChild>
                  <Link
                    to={service.href}
                    className={cn(
                      'cursor-pointer rounded-xl px-3 py-2 font-medium',
                      isActive(service.href) && 'text-elev8-blue'
                    )}
                  >
                    {service.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavItem href="/industries" isActive={isActive('/industries')}>
            INDUSTRIES
          </NavItem>

          <NavItem href="/about" isActive={isActive('/about')}>
            ABOUT
          </NavItem>
        </div>

        {/* Desktop CTA Button */}
        <div
          className={desktopNavButtonsClasses}
          style={{
            display: window.innerWidth >= 768 ? 'flex' : 'none',
          }}
        >
          <ContactFormButton
            className="bg-blue-500 text-white px-4 py-2 rounded-full hover:bg-blue-600 transition-colors"
            ctaName="schedule_demo"
            ctaLocation="navbar"
            offerType="consultation"
            serviceContext="general"
          >
            Schedule a Demo
          </ContactFormButton>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden theme-nav__icon text-base sm:text-lg p-2 focus:outline-none rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
          onClick={toggleMobileMenu}
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
          type="button"
        >
          <Menu size={24} className="w-6 h-6" />
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <div
        className={cn(
          'mobile-nav-overlay fixed inset-0 flex flex-col items-center justify-center space-y-6 md:hidden transition-all duration-300 z-40 p-4',
          mobileMenuOpen
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 translate-x-full pointer-events-none'
        )}
        onClick={event => {
          if (event.target === event.currentTarget) {
            closeMobileMenu();
          }
        }}
      >
        <button
          className="mobile-nav__close absolute top-6 right-6 p-2 focus:outline-none"
          onClick={closeMobileMenu}
          aria-label="Close menu"
          type="button"
        >
          <X size={28} />
        </button>

        <div className="mb-8">
          <Link to="/" onClick={closeMobileMenu} aria-label="Elev8 Home">
            <Logo className="h-14 w-auto" />
          </Link>
        </div>

        <MobileNavItem
          href="/"
          onClick={closeMobileMenu}
          isActive={location.pathname === '/'}
        >
          HOME
        </MobileNavItem>

        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={() => setMobileServicesOpen(prev => !prev)}
            className={cn(
              'mobile-nav__link inline-flex items-center gap-2 text-2xl py-3 px-6 rounded-full transition-all duration-300 font-medium',
              servicesActive && 'mobile-nav__link--active'
            )}
            aria-expanded={mobileServicesOpen}
            aria-controls="mobile-services-menu"
          >
            SERVICES
            <ChevronDown
              size={22}
              className={cn(
                'transition-transform duration-200',
                mobileServicesOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>

          {mobileServicesOpen && (
            <div
              id="mobile-services-menu"
              className="mt-1 flex flex-col items-center space-y-1"
            >
              {SERVICE_LINKS.map(service => (
                <MobileNavItem
                  key={service.href}
                  href={service.href}
                  onClick={closeMobileMenu}
                  isActive={isActive(service.href)}
                  className="text-xl"
                >
                  {service.label}
                </MobileNavItem>
              ))}
            </div>
          )}
        </div>

        <MobileNavItem
          href="/industries"
          onClick={closeMobileMenu}
          isActive={isActive('/industries')}
        >
          INDUSTRIES
        </MobileNavItem>

        <MobileNavItem
          href="/about"
          onClick={closeMobileMenu}
          isActive={isActive('/about')}
        >
          ABOUT
        </MobileNavItem>

        <ContactFormButton
          className="mt-6 bg-blue-500 text-white px-6 py-3 rounded-full hover:bg-blue-600 transition-colors"
          ctaName="schedule_demo"
          ctaLocation="mobile_nav"
          offerType="consultation"
          serviceContext="general"
          onClick={closeMobileMenu}
        >
          Schedule a Demo
        </ContactFormButton>
      </div>
    </div>
  );
};

interface NavItemProps {
  href: string;
  children: React.ReactNode;
  isActive: boolean;
}

const NavItem = ({ href, children, isActive }: NavItemProps) => (
  <Link
    to={href}
    className={cn('theme-nav__link', isActive && 'theme-nav__link--active')}
  >
    {children}
  </Link>
);

interface MobileNavItemProps extends NavItemProps {
  onClick: () => void;
  className?: string;
}

const MobileNavItem = ({
  href,
  children,
  onClick,
  isActive,
  className,
}: MobileNavItemProps) => (
  <Link
    to={href}
    className={cn(
      'mobile-nav__link text-2xl py-3 px-6 rounded-full transition-all duration-300 font-medium',
      isActive && 'mobile-nav__link--active',
      className
    )}
    onClick={onClick}
  >
    {children}
  </Link>
);

export default Navbar;
